import type {
  MediaAsset,
  UserApplication,
  ValueAnswers,
  VerificationBundle,
} from "./types";
import type { ProfileAnswers } from "./answers";
import type { FlowAnswers } from "../flow/steps";
import { INTENT_OPTIONS, PHOTO_MIN, rulesAccepted } from "../flow/steps";

function uid(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function resolveIntentLabel(intentId: string | null): string | null {
  if (!intentId) return null;
  const opt = INTENT_OPTIONS.find((o) => o.id === intentId);
  return opt ? `${opt.label} — ${opt.description}` : intentId;
}

function scaleLine(label: string, n: number | null): string | null {
  if (n == null) return null;
  return `${label}: ${n}/10`;
}

function buildValueAnswers(answers: FlowAnswers): ValueAnswers {
  return {
    loyalty: scaleLine("Loyalty", answers.scales.loyalty),
    honesty: scaleLine("Honesty", answers.scales.honesty),
    passion: scaleLine("Passion", answers.scales.passion),
    commitment: scaleLine("Commitment", answers.scales.commitment),
  };
}

/**
 * Map onboarding answers into a backend-shaped UserApplication.
 * `userId` must be the stable AuthUser.id so review/pool/likes share one identity.
 * `profileAnswers` is optional so verify-submit (before the questionnaire) still works.
 */
export function buildApplicationFromAnswers(
  answers: FlowAnswers,
  userId: string,
  profileAnswers?: ProfileAnswers
): UserApplication {
  const now = new Date().toISOString();
  const id = userId;
  const mediaComplete = mediaReady({
    governmentId: answers.governmentId,
    confirmationVideo: answers.confirmationVideo,
    profilePhotos: answers.profilePhotos,
    minPhotos: PHOTO_MIN,
  });

  const verification: VerificationBundle = {
    id: uid("ver"),
    userId: id,
    status: mediaComplete ? "pending_review" : "draft",
    governmentId: answers.governmentId,
    confirmationVideo: answers.confirmationVideo,
    profilePhotos: answers.profilePhotos,
    submittedAt: mediaComplete ? now : null,
    reviewedAt: null,
    reviewerId: null,
    reviewNote: null,
  };

  const accepted = rulesAccepted(answers);

  return {
    id,
    userId: id,
    status: mediaComplete ? "pending_review" : "onboarding",
    createdAt: now,
    updatedAt: now,
    isChristian: answers.isChristian,
    rules: {
      ...answers.rules,
      acceptedAt: accepted ? now : null,
    },
    intent: resolveIntentLabel(answers.intent),
    email: answers.email,
    passwordSet: answers.password.length >= 8,
    verification,
    whyChristian: answers.whyChristian.trim(),
    scales: { ...answers.scales },
    valueAnswers: buildValueAnswers(answers),
    aboutYou: [answers.aboutYou.trim(), answers.lifestyleNotes.trim()]
      .filter(Boolean)
      .join("\n\n"),
    lookingFor: answers.lookingFor.trim(),
    loveReady: answers.loveReady,
    hobbies: answers.hobbies.trim(),
    reading: answers.reading,
    profession: answers.profession.trim(),
    firstName: answers.firstName,
    age: answers.age,
    city: answers.city,
    country: answers.country.trim().toUpperCase(),
    preferredPartnerCountry: answers.preferredPartnerCountry || "any",
    gender: answers.gender,
    seeking:
      answers.gender === "male"
        ? "female"
        : answers.gender === "female"
          ? "male"
          : null,
    ...(profileAnswers
      ? {
          answers: {
            ...profileAnswers,
            free: {
              ...profileAnswers.free,
              describeYourself:
                profileAnswers.free.describeYourself.trim() ||
                answers.aboutYou.trim(),
            },
          },
        }
      : {}),
  };
}

export function mediaReady(v: {
  governmentId: MediaAsset | null;
  confirmationVideo: MediaAsset | null;
  profilePhotos: MediaAsset[];
  minPhotos: number;
}): boolean {
  return (
    v.governmentId?.reviewStatus === "pending_review" &&
    v.confirmationVideo?.reviewStatus === "pending_review" &&
    v.profilePhotos.length >= v.minPhotos &&
    v.profilePhotos.every((p) => p.reviewStatus === "pending_review")
  );
}
